import { ArrowUpRight, Building, Building2, Landmark, Map, Droplets } from "lucide-react";

import {
  LEVEL_LABELS,
  type JurisdictionLayer,
  type JurisdictionLevel,
  type ResolveResult,
} from "@/lib/jurisdiction";
import { cn } from "@/lib/utils";

const LEVEL_ICONS: Record<JurisdictionLevel, React.ComponentType<{ className?: string }>> = {
  federal: Landmark,
  state: Building2,
  county: Map,
  municipality: Building,
  special_district: Droplets,
};

/**
 * The jurisdiction stack — Prosa's signature element. A vertical stack of
 * bordered rows, federal at top descending to local / special districts at
 * the bottom, each row showing the level, the jurisdiction name, an
 * in-coverage badge, and a source link.
 */
export function JurisdictionStack({
  result,
  className,
}: {
  result: ResolveResult;
  className?: string;
}) {
  return (
    <div className={cn("overflow-hidden rounded-[10px] border border-border bg-surface", className)}>
      {result.normalized_address ? (
        <div className="border-b border-border bg-paper/60 px-4 py-3 sm:px-5">
          <p className="overline">Resolved address</p>
          <p className="mt-0.5 cite text-ink">{result.normalized_address}</p>
        </div>
      ) : null}
      <ol className="divide-y divide-border">
        {result.layers.map((layer, idx) => (
          <LayerRow key={`${layer.level}-${idx}`} layer={layer} index={idx} />
        ))}
      </ol>
    </div>
  );
}

function LayerRow({ layer, index }: { layer: JurisdictionLayer; index: number }) {
  const Icon = LEVEL_ICONS[layer.level] ?? Building;
  return (
    <li className="flex items-start gap-3 px-4 py-3.5 sm:gap-4 sm:px-5">
      {/* Hierarchy rail: indent deepens as we descend the stack. */}
      <div
        className="hidden shrink-0 sm:block"
        style={{ width: `${Math.min(index, 4) * 14}px` }}
        aria-hidden="true"
      />
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] border border-border bg-paper text-muted">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-serif text-[12px] font-semibold uppercase tracking-[0.05em] text-ink">
            {LEVEL_LABELS[layer.level] ?? layer.level}
          </span>
          {/* in_scope reflects the RESOLVER's curated seed (jurisdiction
              identified + official source verified), not rules retrieval —
              labelling it "in coverage" implied the latter and contradicted
              the product one click later (2026-08-29 audit). */}
          <span className={cn("badge", layer.in_scope ? "badge-success" : "badge-muted")}>
            {layer.in_scope ? "Source verified" : "Source not verified"}
          </span>
        </div>
        <p className="mt-0.5 font-medium text-ink">{layer.name}</p>
        {layer.source_url ? (
          <a
            href={layer.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-flex items-center gap-1 cite text-primary hover:text-primary-hover hover:underline"
          >
            {sourceLabel(layer.source_url)}
            <ArrowUpRight className="h-3 w-3" />
          </a>
        ) : (
          <p className="mt-1 cite text-muted">Source not yet published</p>
        )}
      </div>
    </li>
  );
}

function sourceLabel(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "Source";
  }
}
